/**
 * Expanded-row detail bodies for the drawer's Issues list - the eager
 * opened-from variant (envelope detail_blocks, no fetch) and the lazy
 * by-tag sibling variant. Split out of IssuesEntryList.tsx (one file per
 * concern); both reuse ExceptionDetailsSection (Why) + FixResourceLinks (How).
 *
 * The body is left-aligned with the row's TITLE text (``indent``/``padRight``
 * come from IssuesEntryRow) so Why and the fix links line up under the issue
 * name. The marked-fixed marker + detected date live on the row's own second
 * line, and the business-type icon now rides the header beside the title
 * (IssuesEntryRow), so the body opens straight into the (un-captioned) Why text.
 */
import React from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import type { IssuesChipEntry } from './issuesState'
import type { ExceptionDrawerDetailBlock } from '../types'
import ExceptionDetailsSection from '../../../components/inspector/shared/exceptionDrawer/ExceptionDetailsSection'
import FixResourceLinks from '../../../components/inspector/shared/exceptionDrawer/FixResourceLinks'
import { useExceptionDetailBlocks } from '../../../hooks/useExceptionDetailBlocks'

// Default body indents (non-dense open list); IssuesEntryRow overrides per row
// so the body aligns with the title for dense (closed-history) rows too.
const DEFAULT_INDENT = 48
const DEFAULT_PAD_RIGHT = 20

// Eager Why + How for the focused row, rendered from the envelope's
// detail_blocks (+ the row's own links) - no by-tag fetch. The body opens with
// Why; How follows (whitespace separates).
export const OpenedFromDetail: React.FC<{
  blocks: ExceptionDrawerDetailBlock[]
  links: IssuesChipEntry['links']
  indent?: number
  padRight?: number
}> = ({ blocks, links, indent = DEFAULT_INDENT, padRight = DEFAULT_PAD_RIGHT }) => {
  return (
    <Box sx={{ pl: `${indent}px`, pr: `${padRight}px`, pt: '8px', pb: '14px' }}>
      <ExceptionDetailsSection details={blocks} showTopDivider={false} tightTop />
      <FixResourceLinks links={links} />
    </Box>
  )
}

// Lazy Why + How for one sibling tag, fetched on expand. Reuses the same
// renderer (ExceptionDetailsSection) + How builder (buildHowToFixBlocks) as
// OpenedFromDetail.
export const SiblingDetail: React.FC<{
  fpCode: string
  tagId: number
  indent?: number
  padRight?: number
}> = ({ fpCode, tagId, indent = DEFAULT_INDENT, padRight = DEFAULT_PAD_RIGHT }) => {
  const { detailBlocks, links, loading, error } = useExceptionDetailBlocks(
    fpCode,
    tagId,
    true,
  )

  return (
    <Box sx={{ pl: `${indent}px`, pr: `${padRight}px`, pt: '8px', pb: '14px' }}>
      {/* Soft skeleton while the by-tag fetch is in flight (mirrors the
        * inspector dashboard's loading blocks) - never a flash of placeholder
        * text. When the load resolves with no Why/How, both sections render
        * null and the body simply stays empty. */}
      {loading && (
        <Box aria-busy="true">
          <Skeleton variant="text" width="40%" height={14} />
          <Skeleton variant="rounded" height={44} sx={{ mt: '6px' }} />
        </Box>
      )}
      {!loading && error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      {!loading && !error && (
        <>
          <ExceptionDetailsSection
            details={detailBlocks}
            showTopDivider={false}
            tightTop
          />
          <FixResourceLinks links={links} />
        </>
      )}
    </Box>
  )
}
